import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { AssetView, AssetStatus, AssetCondition } from "../../../../api/General/assets";
import { Card, CardContent } from "@/components/ui/Card";

interface AssetTableProps {
  assets: AssetView[];
  loading: boolean;
  onRowClick: (asset: AssetView) => void;
  sortBy: string;
  sortDescending: boolean;
  onSort: (field: string) => void;
}

const statusStyles: Record<AssetStatus, string> = {
  [AssetStatus.Available]: "bg-green-100 text-green-800",
  [AssetStatus.Issued]: "bg-blue-100 text-blue-800",
  [AssetStatus.UnderMaintenance]: "bg-yellow-100 text-yellow-800",
  [AssetStatus.Retired]: "bg-muted text-muted-foreground",
};

const conditionStyles: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "bg-emerald-100 text-emerald-800",
  [AssetCondition.Good]: "bg-blue-100 text-blue-800",
  [AssetCondition.Fair]: "bg-orange-100 text-orange-800",
  [AssetCondition.Poor]: "bg-red-100 text-red-800",
};

const AssetTable: React.FC<AssetTableProps> = ({
  assets,
  loading,
  onRowClick,
  sortBy,
  sortDescending,
  onSort,
}) => {
  const { t } = useTranslation();

  const statusLabel = (s: AssetStatus) => {
    switch (s) {
      case AssetStatus.Available: return t("assets.statusAvailable");
      case AssetStatus.Issued: return t("assets.statusIssued");
      case AssetStatus.UnderMaintenance: return t("assets.statusUnderMaintenance");
      case AssetStatus.Retired: return t("assets.statusRetired");
      default: return "";
    }
  };

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="ms-1 w-3.5 h-3.5 text-muted-foreground/60" aria-hidden="true" />;
    }
    return sortDescending
      ? <ArrowDown className="ms-1 w-3.5 h-3.5 text-foreground" aria-hidden="true" />
      : <ArrowUp className="ms-1 w-3.5 h-3.5 text-foreground" aria-hidden="true" />;
  };

  const columns = [
    { key: "name", label: t("assets.name") },
    { key: "category", label: t("assets.category") },
    { key: "serialNumber", label: t("assets.serialNumber") },
    { key: "value", label: t("assets.value") },
    { key: "status", label: t("assets.status") },
    { key: "condition", label: t("assets.condition") },
    { key: "assignedTo", label: t("assets.assignedTo") },
  ];

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-8 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (assets.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
        <p className="text-muted-foreground text-lg font-medium">{t("assets.noAssets")}</p>
        <p className="text-muted-foreground text-sm mt-1">{t("assets.noAssetsDescription")}</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden sm:block">
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="asset-table">
              <thead>
                <tr className="border-b border-border bg-muted">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted select-none"
                      onClick={() => onSort(col.key)}
                    >
                      <span className="flex items-center">
                        {col.label}
                        <SortIcon field={col.key} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="hover:bg-muted cursor-pointer transition-colors"
                    onClick={() => onRowClick(asset)}
                    data-testid={`asset-row-${asset.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-foreground">{asset.name}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {asset.categoryName ?? "-"}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono text-foreground">
                        {asset.serialNumber ?? "-"}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {asset.purchasePrice != null
                        ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(asset.purchasePrice)
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[asset.status]}`}>
                        {statusLabel(asset.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${conditionStyles[asset.condition]}`}>
                        {conditionLabel(asset.condition)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {asset.assignedToUserName ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile card fallback */}
      <div className="sm:hidden space-y-3">
        {assets.map((asset) => (
          <Card
            key={asset.id}
            variant="interactive"
            onClick={() => onRowClick(asset)}
            className="cursor-pointer"
            data-testid={`asset-row-${asset.id}`}
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{asset.name}</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[asset.status]}`}>
                  {statusLabel(asset.status)}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">{asset.categoryName}</div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {asset.serialNumber ? (
                    <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono text-foreground">{asset.serialNumber}</code>
                  ) : (
                    "-"
                  )}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conditionStyles[asset.condition]}`}>
                  {conditionLabel(asset.condition)}
                </span>
              </div>
              {asset.assignedToUserName && (
                <div className="text-xs text-muted-foreground">{t("assets.assignedTo")}: {asset.assignedToUserName}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default AssetTable;
